/**
 * Word count status bar component for the editor
 */

import { cn } from '@/lib/utils';
import { countCharactersInPlateContent, countWordsInPlateContent } from '@/utils/word-count';
import { Value } from '@udecode/plate';
import React from 'react';

interface WordCountStatusProps {
  content: Value;
  className?: string;
  showCharacters?: boolean;
}

export function WordCountStatus({
  content,
  className,
  showCharacters = true
}: WordCountStatusProps) {
  const [counts, setCounts] = React.useState({ wordCount: 0, characterCount: 0 });

  // Debounce the word count calculation to avoid heavy operations on every render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Use requestIdleCallback if available to further defer the calculation
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => {
          setCounts({
            wordCount: countWordsInPlateContent(content),
            characterCount: countCharactersInPlateContent(content)
          });
        });
      } else {
        // Fallback for environments without requestIdleCallback
        setCounts({
          wordCount: countWordsInPlateContent(content),
          characterCount: countCharactersInPlateContent(content)
        });
      }
    }, 1000); // 1 second delay - user won't notice immediate updates while typing fast

    return () => clearTimeout(timer);
  }, [content]);

  return (
    <div className={cn(
      "flex items-center gap-3 px-2 py-0 text-[10px] text-muted-foreground/60 font-mono tracking-tight",
      className
    )}>
      <div className="flex items-center gap-1">
        <span className="font-medium text-foreground/80">{counts.wordCount.toLocaleString()}</span>
        <span>w</span>
      </div>

      {showCharacters && (
        <div className="flex items-center gap-1 before:content-['•'] before:mr-1 before:opacity-30">
          <span className="font-medium text-foreground/80">{counts.characterCount.toLocaleString()}</span>
          <span>ch</span>
        </div>
      )}
    </div>
  );
}
